import { stageActions, stageStudyActions } from '@core/actions/stage.actions';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { createReducer, on } from '@ngrx/store';
import {
  PoolSwitchStatus,
  StudyReviewStatus,
} from '@core/services/api-client.generated';
import { Loading, mapValues, throwErrorExp } from '@core/actions/util';

interface StageStatsStatuses {
  reviewerAnnotationStatsStatus: Loading.State<boolean> | null;
  membershipAnnotationStatsStatus: Loading.State<null> | null;
  stageAnnotationStatsStatus: Loading.State<null> | null;
}
export interface StageUIState {
  loadedStudyId: Loading.State<{
    studyId: string | null;
    reviewStatus: StudyReviewStatus;
    poolSwitchStatus: PoolSwitchStatus;
  } | null>;
  currentStageId: string | null;
  sessionSaving: boolean;
  statsStatuses: {
    [stageId: string]: StageStatsStatuses;
  };
}
const initialStageStatsStatuses: StageStatsStatuses = {
  reviewerAnnotationStatsStatus: null,
  membershipAnnotationStatsStatus: null,
  stageAnnotationStatsStatus: null,
} as const;
export const initialStageUIState: StageUIState = {
  loadedStudyId: Loading.successState(null),
  currentStageId: null,
  sessionSaving: false,
  statsStatuses: {},
};

export const stageUIReducer = createReducer(
  initialStageUIState,
  on(
    stageActions.revertSession,
    stageActions.deleteSession,
    (state, props) => ({
      ...state,
    })
  ),
  on(stageActions.receivedFullStats, (state, props) => ({
    ...state,
  })),
  on(projectDetailActions.switchCurrentStage, (state, props) => ({
    ...state,
    currentStageId: props.stageId,
  })),
  on(projectDetailActions.loadStage, (state, props) => ({
    ...state,
    currentStageId: props.stageId,
  })),
  on(projectDetailActions.stageAdded, (state, props) => ({
    ...state,
    statsStatuses: {
      ...state.statsStatuses,
      [props.stageId]: {
        ...initialStageStatsStatuses,
        ...state.statsStatuses[props.stageId],
      },
    },
  })),
  on(
    stageActions.loadStageStudy,
    stageActions.getNextStudyForStage,
    stageActions.submitScreeningAndNextStudy,
    (state, props) => ({
      ...state,
      loadedStudyId: Loading.loadingState(),
      reviewStatus: null,
      poolSwitch: null,
    })
  ),
  on(stageActions.saveSession, (state, props) =>
    props.correlationId !== undefined
      ? state
      : {
          ...state,
          sessionSaving: true,
        }
  ),
  on(stageActions.sessionSaved, (state, props) =>
    props.correlationId !== undefined
      ? state
      : {
          ...state,
          sessionSaving: false,
        }
  ),
  on(stageStudyActions.receivedStageStudy, (state, props) => ({
    ...state,
    loadedStudyId: Loading.successState({
      studyId: props.studyId,
      poolSwitchStatus: props.reviewStatus.poolSwitchStatus,
      reviewStatus: props.reviewStatus.studyReviewStatus,
    }),
    studyReviewStatus: props.reviewStatus.studyReviewStatus,
    poolSwitch: props.reviewStatus.poolSwitchStatus,
  })),
  on(stageStudyActions.errorReceivingNextStudy, (state, props) => ({
    ...state,
  })),
  on(stageStudyActions.noNewStudiesForStage, (state, props) => ({
    ...state,
    loadedStudyId: Loading.successState({
      studyId: null,
      reviewStatus: props.reviewStatus.studyReviewStatus,
      poolSwitchStatus: props.reviewStatus.poolSwitchStatus,
    }),
    studyReviewStatus: props.reviewStatus.studyReviewStatus,
    poolSwitch: props.reviewStatus.poolSwitchStatus,
  })),
  on(projectDetailActions.detailLoaded, (state, props) => {
    const oldStageStatsStatusMap = state.statsStatuses;
    return {
      ...state,
      statsStatuses: mapValues(
        props.entities.stages ?? {},
        (stage) =>
          oldStageStatsStatusMap[stage.id] ?? { ...initialStageStatsStatuses }
      ),
    };
  }),
  on(projectDetailActions.retrieveProjectReviewerStats, (state, props) => ({
    ...state,
    statsStatuses: mapValues(state.statsStatuses, (oldState) => ({
      ...oldState,
      reviewerAnnotationStatsStatus: Loading.loadingState(),
    })),
  })),
  on(stageActions.retrieveStageReviewerStats, (state, props) => ({
    ...state,
    statsStatuses: mapValues(state.statsStatuses, (oldState) => ({
      ...oldState,
      reviewerAnnotationStatsStatus: Loading.loadingState(),
    })),
  })),
  on(stageActions.stageReviewerStatsDelegated, (state): StageUIState => ({
    ...state,
    statsStatuses: mapValues(state.statsStatuses, (oldState) => ({
      ...oldState,
      // No legacy request is in flight; let a later legacy route load fresh rows.
      reviewerAnnotationStatsStatus: null,
    })),
  })),
  on(projectDetailActions.receivedProjectReviewerStats, (state, props) => ({
    ...state,
    statsStatuses: mapValues(
      state.statsStatuses,
      (oldStageStatuses, stageId) => ({
        ...oldStageStatuses,
        reviewerAnnotationStatsStatus: Loading.successState(
          !!props.entities.reviewerAnnotationStats?.[stageId]
        ),
      })
    ),
  })),
  on(stageActions.receivedStageReviewerStats, (state, props) => ({
    ...state,
    statsStatuses: {
      ...state.statsStatuses,
      [props.stageId]: {
        ...initialStageStatsStatuses,
        ...state.statsStatuses[props.stageId],
        reviewerAnnotationStatsStatus: Loading.successState(
          !!props.entities.reviewerAnnotationStats?.[props.stageId]
        ),
      },
    },
  })),
  on(
    projectDetailActions.errorRetrievingProjectReviewerStats,
    (state, props) => ({
      ...state,
      statsStatuses: mapValues(state.statsStatuses, (oldState, stageId) => ({
        ...oldState,
        reviewerAnnotationStatsStatus: Loading.errorState(props.error),
      })),
    })
  ),
  on(stageActions.errorRetrievingStageReviewerStats, (state, props) => ({
    ...state,
    statsStatuses: {
      ...state.statsStatuses,
      [props.stageId]: {
        ...initialStageStatsStatuses,
        ...state.statsStatuses[props.stageId],
        reviewerAnnotationStatsStatus: Loading.errorState(props.error),
      },
    },
  })),
  on(projectDetailActions.retrieveFullProjectStats, (state, props) => ({
    ...state,
    statsStatuses: mapValues(state.statsStatuses, (oldStageStatsStatuses) => ({
      ...initialStageStatsStatuses,
      ...oldStageStatsStatuses,
      membershipAnnotationStatsStatus: Loading.loadingState(),
      stageAnnotationStatsStatus: Loading.loadingState(),
    })),
  })),
  on(projectDetailActions.fullProjectStatsReceived, (state, props) => {
    return {
      ...state,
      statsStatuses: props.stageIds.reduce(
        (acc, stageId) => ({
          ...acc,
          [stageId]: {
            ...initialStageStatsStatuses,
            ...state.statsStatuses[stageId],
            membershipAnnotationStatsStatus: Loading.successState(
              props.membershipIds.every((membershipId) => {
                const annotationStats =
                  props.entities.membershipAnnotationStats?.[membershipId]
                    ?.stageAnnotationStatsMap?.[stageId];
                if (!annotationStats) {
                  throw Error(
                    'membership stage annotation stats missing from normalised action'
                  );
                }
                return annotationStats;
              })
                ? null
                : throwErrorExp(
                    'membership stage annotation stats not found for all memberships'
                  )
            ),
            stageAnnotationStatsStatus: Loading.successState(
              props.entities.stageAnnotationStats?.[stageId]
                ? null
                : throwErrorExp(
                    'stage annotation stats missing from normalised action'
                  )
            ),
          },
        }),
        {} as { [stageId: string]: StageStatsStatuses }
      ),
    };
  }),
  on(projectDetailActions.errorRetrievingFullProjectStats, (state, props) => ({
    ...state,
    statsStatuses: mapValues(state.statsStatuses, (oldStageStatsStatuses) => ({
      ...initialStageStatsStatuses,
      ...oldStageStatsStatuses,
      membershipAnnotationStatsStatus: Loading.errorState(props.error),
      stageAnnotationStatsStatus: Loading.errorState(props.error),
    })),
  }))
);
